import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { Loader2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import Layout from '@/components/Layout';

// Add the app owner's Supabase user ID(s) here
const ADMIN_USER_IDS: string[] = [
  import.meta.env.VITE_ADMIN_USER_ID ?? '',
].filter(Boolean);

type Period = 7 | 30;

interface RawEvent {
  event_name: string;
  created_at: string;
}

interface DailyCount {
  day: string;
  [event: string]: number | string;
}

const EVENT_COLORS: Record<string, string> = {
  bet_placed: 'hsl(24 95% 53%)',
  bracket_filled: 'hsl(200 80% 55%)',
  group_created: 'hsl(140 60% 50%)',
  group_joined: 'hsl(280 60% 60%)',
  leaderboard_viewed: 'hsl(50 80% 55%)',
  bracket_shared: 'hsl(340 70% 60%)',
  tip_jar_clicked: 'hsl(160 60% 50%)',
  side_bet_placed: 'hsl(30 60% 50%)',
};

function aggregateByDay(events: RawEvent[]): DailyCount[] {
  const map = new Map<string, Record<string, number>>();

  for (const ev of events) {
    const day = ev.created_at.slice(0, 10); // YYYY-MM-DD
    if (!map.has(day)) map.set(day, {});
    const bucket = map.get(day)!;
    bucket[ev.event_name] = (bucket[ev.event_name] ?? 0) + 1;
  }

  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([day, counts]) => ({ day, ...counts }));
}

function countByEventType(events: RawEvent[]): Record<string, number> {
  const result: Record<string, number> = {};
  for (const ev of events) {
    result[ev.event_name] = (result[ev.event_name] ?? 0) + 1;
  }
  return result;
}

const AdminAnalytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [period, setPeriod] = useState<Period>(7);
  const [events, setEvents] = useState<RawEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const isAdmin = user && ADMIN_USER_IDS.includes(user.id);

  useEffect(() => {
    if (!user) return;
    if (!isAdmin) {
      navigate('/');
      return;
    }
    const fetchEvents = async () => {
      setLoading(true);
      const since = new Date(Date.now() - period * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('analytics_events')
        .select('event_name, created_at')
        .gte('created_at', since)
        .order('created_at', { ascending: true });

      if (!error && data) {
        setEvents(data as RawEvent[]);
      }
      setLoading(false);
    };
    fetchEvents();
  }, [user, isAdmin, period, navigate]);

  if (!user || !isAdmin) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-20 text-center">
          <p className="text-xl font-bold text-destructive">אין גישה</p>
          <p className="text-muted-foreground mt-2 text-sm">עמוד זה מוגבל למנהלים בלבד</p>
        </div>
      </Layout>
    );
  }

  const dailyCounts = aggregateByDay(events);
  const totalByType = countByEventType(events);
  const uniqueEvents = Array.from(new Set(events.map(e => e.event_name)));

  return (
    <Layout>
      <div className="container mx-auto px-4 py-10 max-w-5xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <p className="section-label mb-2">ניהול</p>
          <h1 className="text-3xl font-bold tracking-tight mb-6">לוח בקרה — אנליטיקס</h1>

          {/* Period toggle */}
          <div className="flex gap-2 mb-8">
            {([7, 30] as Period[]).map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[40px] ${
                  period === p
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-secondary text-muted-foreground hover:text-foreground'
                }`}
              >
                {p} ימים
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-accent" />
            </div>
          ) : (
            <>
              {/* Daily events bar chart */}
              <div className="bg-card border border-border rounded-2xl p-6 mb-6">
                <h2 className="text-lg font-semibold mb-4">אירועים לפי יום</h2>
                {dailyCounts.length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-10">אין נתונים לתקופה זו</p>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dailyCounts} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
                      <XAxis
                        dataKey="day"
                        tick={{ fill: 'hsl(0 0% 65%)', fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <YAxis
                        tick={{ fill: 'hsl(0 0% 65%)', fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(0 0% 8%)',
                          border: '1px solid hsl(0 0% 15%)',
                          borderRadius: '8px',
                          fontSize: 12,
                        }}
                        labelStyle={{ color: 'hsl(0 0% 80%)', marginBottom: 4 }}
                        cursor={{ fill: 'hsl(0 0% 100% / 0.05)' }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
                        formatter={(value) => (
                          <span style={{ color: 'hsl(0 0% 65%)' }}>{value}</span>
                        )}
                      />
                      {uniqueEvents.map(eventName => (
                        <Bar
                          key={eventName}
                          dataKey={eventName}
                          stackId="a"
                          fill={EVENT_COLORS[eventName] ?? 'hsl(0 0% 50%)'}
                          radius={[0, 0, 0, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Summary cards */}
              <div className="bg-card border border-border rounded-2xl p-6">
                <h2 className="text-lg font-semibold mb-4">סיכום לפי סוג אירוע ({period} ימים)</h2>
                {Object.keys(totalByType).length === 0 ? (
                  <p className="text-muted-foreground text-sm">אין נתונים לתקופה זו</p>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                    {Object.entries(totalByType)
                      .sort(([, a], [, b]) => b - a)
                      .map(([eventName, count]) => (
                        <div
                          key={eventName}
                          className="bg-secondary rounded-xl p-4"
                          style={{ borderLeft: `3px solid ${EVENT_COLORS[eventName] ?? 'hsl(0 0% 50%)'}` }}
                        >
                          <p className="text-2xl font-bold tabular-nums">{count}</p>
                          <p className="text-xs text-muted-foreground mt-1 font-mono">{eventName}</p>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              {/* Total events count */}
              <p className="text-center text-sm text-muted-foreground mt-6">
                סה"כ {events.length} אירועים ב-{period} ימים האחרונים
              </p>
            </>
          )}
        </motion.div>
      </div>
    </Layout>
  );
};

export default AdminAnalytics;
